import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import { teacherApi } from "../../features/teacher/teacherApi";
import {
  TeacherAuthField,
  TeacherAuthLayout,
  TeacherFeedbackMessage,
} from "./TeacherUI";
import { getTeacherAuthErrorMessage } from "../../utils/uiErrorMessages";

export default function TeacherForgotPassword() {
  const [email, setEmail] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalMessage, setGeneralMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const forgotPasswordMutation = useMutation({
    mutationFn: teacherApi.forgotPassword,
    onSuccess: () => {
      setSuccessMessage(
        "If the teacher account exists, the reset flow has been initiated successfully."
      );
      setGeneralMessage("");
      setFieldErrors({});
    },
    onError: (error) => {
      setFieldErrors(error.fieldErrors || {});
      setGeneralMessage(getTeacherAuthErrorMessage(error, "forgot-password"));
      setSuccessMessage("");
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setFieldErrors({});
    setGeneralMessage("");
    setSuccessMessage("");

    if (!email.trim()) {
      setFieldErrors({ email: "Email is required." });
      return;
    }

    forgotPasswordMutation.mutate({
      email: email.trim(),
    });
  };

  return (
    <TeacherAuthLayout
      title="Forgot Password"
      subtitle="Submit your teacher email to start the password reset flow."
      closeTo="/teacherlogin"
      footer={
        <p className="teacher-auth-footer-text">
          Remembered your password?{" "}
          <Link className="teacher-auth-link" to="/teacherlogin">
            Back to login
          </Link>
        </p>
      }
    >
      {generalMessage ? (
        <TeacherFeedbackMessage variant="error">{generalMessage}</TeacherFeedbackMessage>
      ) : null}
      {successMessage ? (
        <TeacherFeedbackMessage variant="success">{successMessage}</TeacherFeedbackMessage>
      ) : null}

      <form className="teacher-auth-form" onSubmit={handleSubmit} noValidate>
        <TeacherAuthField
          id="teacher-forgot-email"
          label="Email Address"
          icon={Mail}
          name="email"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (fieldErrors.email) setFieldErrors({});
          }}
          placeholder="Enter your teacher email"
          error={fieldErrors.email}
        />

        <button
          className="teacher-auth-button"
          type="submit"
          disabled={forgotPasswordMutation.isPending}
        >
          {forgotPasswordMutation.isPending ? "Sending..." : "Send reset request"}
        </button>
      </form>
    </TeacherAuthLayout>
  );
}
